'use client';
import { useCallback, useEffect, useState } from 'react';
import { fetchWithCache, invalidateResource } from '@/lib/use-resource';

/**
 * Deck-style portfolio scorecard. Mirrors the slide that build_v14.py emits
 * but with LIVE visualizer data (shares × today's price for weights, current
 * scores.json for per-holding Q / V / pillar scores). Always shows today's
 * portfolio in deck format so Steve can screenshot or paste straight into
 * the presentation.
 *
 * Designed for screenshot. Dark navy background, gold accent rules,
 * green / red score coloring matching the slide convention.
 */

interface ApiStock {
  symbol: string;
  name?: string;
  qualityScore: number | null;
  /** Production blended Q = 0.75·peer-z + 0.25·universe-z absolute (MSFT 0.67). */
  qualityScoreBlended?: number | null;
  /** Peer-relative V z-score (the original V; "Rel V" in the table). */
  valuationScore: number | null;
  /** Universe-z absolute V (vAbsoluteC, written by blend-into-scores.ts).
   *  Steve directive 2026-05-10: surface alongside the peer-z and blended V. */
  valuationScoreAbsolute?: number | null;
  /** Production blended V = 0.75·peer-z + 0.25·universe-z absolute. */
  valuationScoreBlended?: number | null;
  pillarScores?: {
    profitability?: number | null;
    growth?: number | null;
    safety?: number | null;
    shareholderReturns?: number | null;
  };
}

interface ApiPortfolio {
  symbols: string[];
  weights?: Record<string, number>;
  cashWeight?: number;
}

// /api/self-percentiles per-symbol — ax keys are optional because the four
// pillar axes weren't tracked before 2026-05-05 (window builds forward).
interface SelfAxis { current: number; percentile: number | null; count: number; regime: string }
interface SelfPercentileEntry {
  q?: SelfAxis; v?: SelfAxis; composite?: SelfAxis;
  profitability?: SelfAxis; growth?: SelfAxis;
  safety?: SelfAxis; shareholderReturns?: SelfAxis;
}
interface SelfPercentilesFile {
  bySymbol?: Record<string, SelfPercentileEntry>;
}

const tableHeader = ['Stock', 'Company', 'Weight', 'Profitability', 'Growth', 'Safety', 'Payout', 'Quality', 'Rel V', 'Abs V', 'Comb V', 'Composite'];

function colorFor(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '#94a3b8';
  if (v >= 0.5) return '#4ade80';   // bright green
  if (v > 0) return '#86efac';      // light green
  if (v >= -0.5) return '#94a3b8';  // muted
  return '#f87171';                 // red
}

function fmt(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}`;
}

interface ScorecardRow {
  symbol: string;
  company: string;
  weightPct: number | null;
  quality: number | null;
  profitability: number | null;
  growth: number | null;
  safety: number | null;
  payout: number | null;        // shareholderReturns pillar — labeled "Payout" on the slide
  valuationRel: number | null;  // peer-z V (original valuationScore)
  valuationAbs: number | null;  // universe-z absolute V (vAbsoluteC)
  valuationComb: number | null; // blended V = 0.75 rel + 0.25 abs (production)
  composite: number | null;     // computed against COMBINED V (production)
  qMix: number | null;          // share of composite from the α-scaled Q axis (0..1)
  vMix: number | null;          // share of composite from the β-scaled V axis (0..1)
  // Self-percentiles per axis (vs own multi-year history). Pillars are
  // optional + sparse — the pillar archive started 2026-05-05 and grows daily.
  pctile?: {
    quality?: number | null;
    profitability?: number | null;
    growth?: number | null;
    safety?: number | null;
    payout?: number | null;
    valuation?: number | null;
    composite?: number | null;
  };
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function pctileColor(p: number | null | undefined): string {
  if (p == null) return '#64748b';
  if (p >= 80) return '#4ade80';
  if (p >= 60) return '#86efac';
  if (p >= 40) return '#94a3b8';
  if (p >= 20) return '#fb923c';
  return '#f87171';
}

/** Render the cell value with a tiny percentile pill below. The pill goes
 *  dim ('—') when the axis has no archive yet (e.g. pillars before
 *  2026-05-05). */
function FactorCell({ value, percentile, bold = false }: { value: number | null; percentile?: number | null | undefined; bold?: boolean }) {
  return (
    <td className="py-0.5 text-right tabular-nums leading-tight">
      <div className={bold ? 'font-bold' : ''} style={{ color: colorFor(value) }}>{fmt(value)}</div>
      <div className="text-[8px] tabular-nums" style={{ color: pctileColor(percentile) }} title={percentile == null ? 'No history archive yet' : `${ordinal(Math.round(percentile))} percentile vs own history`}>
        {percentile == null ? '—' : ordinal(Math.round(percentile))}
      </div>
    </td>
  );
}

// Per-symbol display-name override (mirrors generate-portfolio-pdf.py).
const NAME_OVERRIDES: Record<string, string> = {
  TSM: 'Taiwan Semiconductor',
};

function firstWord(symbol: string, fullName: string | null | undefined): string {
  const override = NAME_OVERRIDES[symbol];
  if (override) return override.split(/\s+/)[0];
  if (!fullName) return symbol;
  return fullName.split(/\s+/)[0].replace(/[,.]$/, '');
}

import { compositeScore, prodQuality, QUALITY_SCALE, VALUATION_BETA, VALUATION_SHIFT } from '@/lib/composite-score';

/** Share of the composite from each axis (α-scaled Q vs β-scaled V).
 *  Computed on the SAME scaled axes the composite uses. */
function computeMix(q: number | null, v: number | null): { qMix: number | null; vMix: number | null } {
  if (q == null || v == null) return { qMix: null, vMix: null };
  const qScaled = QUALITY_SCALE * q;
  const vScaled = VALUATION_BETA * (v + VALUATION_SHIFT);
  const denom = Math.abs(qScaled) + Math.abs(vScaled);
  if (!(denom > 0)) return { qMix: null, vMix: null };
  return { qMix: Math.abs(qScaled) / denom, vMix: Math.abs(vScaled) / denom };
}

interface ScorecardProps {
  /** Override the symbol list. Defaults to /api/portfolio.symbols. Pass the
   *  alpha basket's slot symbols to render a basket-scoped scorecard. */
  symbols?: string[];
  /** Override the title shown in the section header. Default: "Portfolio Scorecard". */
  title?: string;
}

export function PortfolioScorecard({ symbols: symbolsOverride, title }: ScorecardProps = {}) {
  const [stocks, setStocks] = useState<Map<string, ApiStock> | null>(null);
  const [portfolio, setPortfolio] = useState<ApiPortfolio | null>(null);
  const [pctiles, setPctiles] = useState<SelfPercentilesFile | null>(null);
  const [universeCount, setUniverseCount] = useState<number>(1099);
  const [loading, setLoading] = useState(true);
  const [, setLastUpdated] = useState<Date | null>(null);
  const [, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);

  // Fetch from APIs (no FMP hit) — re-derives weights from current scores.json.
  const fetchData = useCallback(async () => {
    const [stocksRes, portRes, pctRes] = await Promise.all([
      fetchWithCache<{ stocks?: ApiStock[]; count?: number }>('/api/stocks').catch(() => ({}) as { stocks?: ApiStock[]; count?: number }),
      fetchWithCache<ApiPortfolio>('/api/portfolio').catch(() => null as ApiPortfolio | null),
      fetchWithCache<SelfPercentilesFile>('/api/self-percentiles').catch(() => null as SelfPercentilesFile | null),
    ]);
    const arr = stocksRes?.stocks ?? [];
    const m = new Map<string, ApiStock>();
    for (const s of arr) m.set(s.symbol, s);
    setStocks(m);
    if (typeof stocksRes?.count === 'number') setUniverseCount(stocksRes.count);
    setPortfolio(portRes);
    setPctiles(pctRes);
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  // Refresh prices server-side (FMP bulk quote, ~1s) then re-fetch APIs.
  const refreshLive = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetch('/api/refresh-prices', { method: 'POST' });
      // Server rewrote /api/stocks + /api/portfolio inputs — drop cache so
      // the next fetchData() actually hits the network instead of returning
      // the stale snapshot.
      invalidateResource('/api/stocks');
      invalidateResource('/api/portfolio');
      await fetchData();
    } finally {
      setRefreshing(false);
    }
  }, [fetchData]);

  // Initial load.
  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 60s.
  useEffect(() => {
    const id = setInterval(() => refreshLive(), 60_000);
    return () => clearInterval(id);
  }, [refreshLive]);

  // Tick once a second so the "X seconds ago" label stays current.
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;

  if (loading) return <div className="text-[12px] text-[#64748b]">Loading scorecard…</div>;
  if (!stocks || !portfolio) return null;

  // When `symbols` prop is provided (e.g. /alpha-zone passing the basket
  // slots), it replaces the portfolio's symbols entirely. Weights become
  // equal across the override set; cashPct goes to 0 because the basket
  // doesn't carry cash.
  const overrideActive = Array.isArray(symbolsOverride) && symbolsOverride.length > 0;
  const symbols = overrideActive ? symbolsOverride! : portfolio.symbols;
  const equalWeight = overrideActive ? 100 / symbols.length : null;
  const weights = portfolio.weights ?? {};
  const cashPct = overrideActive ? 0 : (portfolio.cashWeight ?? 0) * 100;

  const rows: ScorecardRow[] = symbols.map((sym) => {
    const s = stocks.get(sym);
    // Q axis = production blended (0.75 peer-z + 0.25 universe-absolute; MSFT 0.67).
    // This `q` is BOTH the displayed Quality column AND fed to compositeScore below.
    const q = s ? prodQuality(s) : null;
    const vRel = s?.valuationScore ?? null;
    const vAbs = s?.valuationScoreAbsolute ?? null;
    // Use the blended V for composite — that's what Alpha-Zone selection uses,
    // so the Scorecard's composite matches what the model actually picks on.
    // Falls back to peer-z when blended isn't computed (banks, missing data).
    const vComb = s?.valuationScoreBlended ?? vRel;
    const sp = pctiles?.bySymbol?.[sym];
    return {
      symbol: sym,
      company: firstWord(sym, s?.name),
      weightPct: equalWeight ?? (weights[sym] != null ? weights[sym] * 100 : null),
      quality: q,
      profitability: s?.pillarScores?.profitability ?? null,
      growth: s?.pillarScores?.growth ?? null,
      safety: s?.pillarScores?.safety ?? null,
      payout: s?.pillarScores?.shareholderReturns ?? null,
      valuationRel: vRel,
      valuationAbs: vAbs,
      valuationComb: vComb,
      composite: compositeScore(q, vComb),
      ...computeMix(q, vComb),
      pctile: sp ? {
        quality: sp.q?.percentile ?? null,
        valuation: sp.v?.percentile ?? null,
        composite: sp.composite?.percentile ?? null,
        profitability: sp.profitability?.percentile ?? null,
        growth: sp.growth?.percentile ?? null,
        safety: sp.safety?.percentile ?? null,
        payout: sp.shareholderReturns?.percentile ?? null,
      } : undefined,
    };
  });

  // Sort by weight descending — largest first.
  rows.sort((a, b) => (b.weightPct ?? 0) - (a.weightPct ?? 0));

  // Weight-averaged portfolio totals — over only stocks that have a value
  // for the metric being aggregated.
  function weightedAvg(getter: (r: ScorecardRow) => number | null): number | null {
    let sumW = 0, sumWX = 0;
    for (const r of rows) {
      const w = r.weightPct;
      const x = getter(r);
      if (w == null || x == null) continue;
      sumW += w;
      sumWX += w * x;
    }
    return sumW > 0 ? sumWX / sumW : null;
  }

  const portfolioRow = {
    quality: weightedAvg(r => r.quality),
    profitability: weightedAvg(r => r.profitability),
    growth: weightedAvg(r => r.growth),
    safety: weightedAvg(r => r.safety),
    payout: weightedAvg(r => r.payout),
    valuationRel: weightedAvg(r => r.valuationRel),
    valuationAbs: weightedAvg(r => r.valuationAbs),
    valuationComb: weightedAvg(r => r.valuationComb),
    composite: weightedAvg(r => r.composite),
  };

  return (
    <section className="bg-[#0a0e14] border border-[#1e293b] rounded-lg overflow-hidden">
      <div className="border-b border-amber-400/40 px-6 pt-5 pb-3 text-center bg-[#0a0e14]">
        <h2 className="text-[20px] md:text-[22px] font-bold text-white tracking-[0.2em] uppercase">
          {title ?? 'Our Portfolio — Scored'}
        </h2>
        <p className="text-[11px] text-[#94a3b8] mt-2">
          Each holding scored against {universeCount} stocks. Standard deviations above market average.
        </p>
      </div>

      <div className="px-6 py-4">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-[#1e293b]">
              {tableHeader.map((h, i) => (
                <th
                  key={h}
                  className={`py-0.5 text-amber-400 uppercase tracking-wider text-[10px] font-semibold ${i === 0 ? 'text-left' : 'text-right'}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.symbol} className="border-b border-[#1e293b]/40">
                <td className="py-0.5 text-white font-mono font-bold">{r.symbol}</td>
                <td className="py-0.5 text-[#cbd5e1]">{r.company}</td>
                <td className="py-0.5 text-right tabular-nums text-[#cbd5e1]">
                  {r.weightPct != null ? `${r.weightPct.toFixed(1)}%` : '—'}
                </td>
                <FactorCell value={r.profitability} percentile={r.pctile?.profitability} />
                <FactorCell value={r.growth} percentile={r.pctile?.growth} />
                <FactorCell value={r.safety} percentile={r.pctile?.safety} />
                <FactorCell value={r.payout} percentile={r.pctile?.payout} />
                <FactorCell value={r.quality} percentile={r.pctile?.quality} />
                <FactorCell value={r.valuationRel} />
                <FactorCell value={r.valuationAbs} />
                <FactorCell value={r.valuationComb} percentile={r.pctile?.valuation} />
                <td className="py-0.5 text-right tabular-nums leading-tight">
                  <div className="font-bold" style={{ color: colorFor(r.composite) }}>{fmt(r.composite)}</div>
                  <div className="text-[8px] tabular-nums" style={{ color: pctileColor(r.pctile?.composite) }} title={r.pctile?.composite == null ? 'No history archive yet' : `${ordinal(Math.round(r.pctile.composite))} percentile vs own history`}>
                    {r.pctile?.composite == null ? '—' : ordinal(Math.round(r.pctile.composite))}
                  </div>
                  <div className="text-[8px] tabular-nums text-[#64748b]" title="Share of the composite from each axis (α-scaled Q vs β-scaled V).">
                    {r.qMix == null || r.vMix == null ? '—' : `Q ${Math.round(r.qMix * 100)}% · V ${Math.round(r.vMix * 100)}%`}
                  </div>
                </td>
              </tr>
            ))}
            {cashPct > 0 && (
              <tr className="border-b border-[#1e293b]/40">
                <td className="py-0.5 text-[#94a3b8] font-mono">Cash</td>
                <td className="py-0.5 text-[#64748b]">—</td>
                <td className="py-0.5 text-right tabular-nums text-[#cbd5e1]">{cashPct.toFixed(1)}%</td>
                <td colSpan={9} className="py-0.5 text-right text-[10px] text-[#64748b] pr-2">uninvested</td>
              </tr>
            )}
            <tr className="border-t border-amber-400/40 bg-[#0d1117]">
              <td className="py-0.5 text-amber-400 font-mono font-bold">PORTFOLIO</td>
              <td className="py-0.5 text-[#cbd5e1]">—</td>
              <td className="py-0.5 text-right text-[#cbd5e1]">—</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.profitability) }}>{fmt(portfolioRow.profitability)}</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.growth) }}>{fmt(portfolioRow.growth)}</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.safety) }}>{fmt(portfolioRow.safety)}</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.payout) }}>{fmt(portfolioRow.payout)}</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.quality) }}>{fmt(portfolioRow.quality)}</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.valuationRel) }}>{fmt(portfolioRow.valuationRel)}</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.valuationAbs) }}>{fmt(portfolioRow.valuationAbs)}</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.valuationComb) }}>{fmt(portfolioRow.valuationComb)}</td>
              <td className="py-0.5 text-right tabular-nums font-bold" style={{ color: colorFor(portfolioRow.composite) }}>{fmt(portfolioRow.composite)}</td>
            </tr>
            <tr>
              <td className="py-0.5 text-[#64748b] font-mono">MARKET</td>
              <td className="py-0.5 text-[#64748b]">—</td>
              <td className="py-0.5 text-right text-[#64748b]">—</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
              <td className="py-0.5 text-right tabular-nums text-[#64748b]">+0.00</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="border-t border-amber-400/40 px-6 py-3 text-center bg-[#0a0e14]">
        <p className="text-[12px] italic text-amber-400">
          Higher quality. Faster growth. More profitable. The premium is justified.
        </p>
      </div>
    </section>
  );
}
